import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { can, errorText, MenuItem, ORDER_TYPE_LABEL, PosMenu, posApi, PosOrder, PosOrderItem, PosTable, rupees } from './api';
import { usePos } from './PosLayout';

const METHODS = ['Cash', 'UPI', 'Card', 'Other'] as const;

// One order: menu on the left, the running ticket on the right. Captains add items and send KOT/BOT;
// cashiers bill and take payments; managers discount, void and cancel.
const OrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id);
  const { me, to } = usePos();
  const navigate = useNavigate();
  const [order, setOrder] = useState<PosOrder | null>(null);
  const [menu, setMenu] = useState<PosMenu | null>(null);
  const [category, setCategory] = useState<number | 'all'>('all');
  const [search, setSearch] = useState('');
  const [picking, setPicking] = useState<MenuItem | null>(null);
  const [voiding, setVoiding] = useState<PosOrderItem | null>(null);
  const [dialog, setDialog] = useState<'discount' | 'cancel' | 'move' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    posApi.order(orderId).then(setOrder).catch((err) => setError(errorText(err, 'Order not found.')));
    posApi.menu().then(setMenu).catch(() => setError('Could not load the menu.'));
  }, [orderId]);

  // Runs an order action and shows the updated order (or the API's message).
  const act = useCallback(async (fn: () => Promise<PosOrder>) => {
    setBusy(true);
    setError(null);
    try {
      setOrder(await fn());
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const items = useMemo(() => {
    if (!menu) return [];
    const q = search.trim().toLowerCase();
    const activeCats = new Set(menu.categories.filter((c) => c.isActive).map((c) => c.id));
    return menu.items.filter((i) => activeCats.has(i.categoryId) && (category === 'all' || i.categoryId === category)
      && (!q || i.name.toLowerCase().includes(q) || (i.code ?? '').toLowerCase().startsWith(q)));
  }, [menu, category, search]);

  if (!order) return <p className="pos-muted">{error ?? 'Loading order...'}</p>;

  const closed = order.status === 'Paid' || order.status === 'Cancelled';
  const unsent = order.items.filter((i) => !i.kotId && !i.isVoid).length;
  const add = (item: MenuItem, variantId?: number) => act(() => posApi.addItem(order.id, item.id, variantId));
  const tap = (item: MenuItem) => (item.variants.length > 1 ? setPicking(item) : add(item));

  const sendKot = async () => {
    setBusy(true);
    setError(null);
    try {
      const kots = await posApi.sendKot(order.id);
      navigate(to(`/pos/print/kot?ids=${kots.map((k) => k.id).join(',')}&back=${encodeURIComponent(`/pos/order/${order.id}`)}`));
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const bill = async () => {
    if (await act(() => posApi.bill(order.id))) navigate(to(`/pos/print/bill/${order.id}?back=${encodeURIComponent(`/pos/order/${order.id}`)}`));
  };

  return (
    <div className="pos-order">
      <section>
        <div className="pos-floor-head" style={{ marginBottom: 10 }}>
          <button className="pos-btn sm" onClick={() => navigate(to('/pos'))}>← Floor</button>
          <input className="pos-input" style={{ flex: 1, minWidth: 180 }} placeholder="Search item or code" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search menu" />
        </div>
        <div className="pos-cats">
          <button className={`pos-tab ${category === 'all' ? 'on' : ''}`} onClick={() => setCategory('all')}>All</button>
          {menu?.categories.filter((c) => c.isActive).map((c) => (
            <button key={c.id} className={`pos-tab ${category === c.id ? 'on' : ''}`} onClick={() => setCategory(c.id)}>{c.name}</button>
          ))}
        </div>
        {menu && menu.items.length === 0 && <div className="pos-panel">The menu is empty. A manager can add items under Setup.</div>}
        <div className="pos-items">
          {items.map((i) => (
            <button key={i.id} className="pos-item" disabled={!i.isAvailable || closed || busy} onClick={() => tap(i)} title={i.isAvailable ? undefined : 'Not available'}>
              <span><span className={`pos-mark ${i.foodType}`} aria-label={i.foodType} />{i.name}</span>
              <small>{i.variants.length > 1 ? i.variants.map((v) => `${v.name} ${rupees(v.price)}`).join(' · ') : rupees(i.variants[0]?.price ?? 0)}{!i.isAvailable ? ' · not available' : ''}</small>
            </button>
          ))}
        </div>
      </section>

      <aside className="pos-panel pos-ticket">
        <div>
          <b style={{ fontSize: 18 }}>#{order.orderNo} · {order.tableName ? `Table ${order.tableName}` : ORDER_TYPE_LABEL[order.orderType]}</b>
          <div className="pos-muted" style={{ fontSize: 13 }}>
            {order.covers} guests{order.customerName ? ` · ${order.customerName}` : ''}{order.customerPhone ? ` · ${order.customerPhone}` : ''} · {order.status}
            {order.billNumber ? ` · Bill ${order.billNumber}` : ''}
          </div>
        </div>
        {error && <div className="pos-msg err" role="alert">{error}</div>}

        <div className="pos-lines">
          {order.items.length === 0 && <p className="pos-muted">Tap menu items to add them.</p>}
          {order.items.map((l) => (
            <div key={l.id} className={`pos-line ${l.isVoid ? 'void' : ''}`}>
              <div>
                <div>{l.name}{l.variantName ? ` (${l.variantName})` : ''}</div>
                <div className="meta">
                  {rupees(l.unitPrice)} × {l.quantity}
                  {l.note ? ` · ${l.note}` : ''}
                  {' · '}{l.isVoid ? `void: ${l.voidReason}` : l.kotId ? <span className="pos-sent">{l.station === 'Bar' ? 'BOT' : 'KOT'} {l.kotNo}</span> : <span className="pos-pending">not sent</span>}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div>{rupees(l.amount)}</div>
                {!closed && !l.isVoid && !l.kotId && (
                  <div className="pos-qty">
                    <button aria-label={`Less ${l.name}`} disabled={busy} onClick={() => act(() => posApi.updateItem(order.id, l.id, l.quantity - 1, l.note))}>−</button>
                    <button aria-label={`More ${l.name}`} disabled={busy} onClick={() => act(() => posApi.updateItem(order.id, l.id, l.quantity + 1, l.note))}>+</button>
                    <button aria-label={`Note for ${l.name}`} disabled={busy} onClick={() => {
                      const note = window.prompt('Note for the kitchen/bar (e.g. less spicy, no ice)', l.note ?? '');
                      if (note !== null) act(() => posApi.updateItem(order.id, l.id, l.quantity, note));
                    }}>✎</button>
                  </div>
                )}
                {!closed && !l.isVoid && l.kotId && can(me.role, 'Manager') && <button className="pos-btn sm red" onClick={() => setVoiding(l)}>Void</button>}
              </div>
            </div>
          ))}
        </div>

        <div className="pos-totals">
          <div><span>Subtotal</span><span>{rupees(order.subtotal)}</span></div>
          {order.discountAmount > 0 && <div><span>Discount{order.discountPercent ? ` ${order.discountPercent}%` : ''}</span><span>−{rupees(order.discountAmount)}</span></div>}
          {order.serviceCharge > 0 && <div><span>Service charge {order.serviceChargePercent}%</span><span>{rupees(order.serviceCharge)}</span></div>}
          {order.taxes.map((t) => <div key={t.label}><span>{t.label}</span><span>{rupees(t.amount)}</span></div>)}
          {order.roundOff !== 0 && <div><span>Round off</span><span>{rupees(order.roundOff)}</span></div>}
          <div className="grand"><span>Total</span><span>{rupees(order.total)}</span></div>
          {order.paidTotal > 0 && <div><span>Paid</span><span>{rupees(order.paidTotal)}</span></div>}
          {order.status === 'Billed' && <div><b>Balance due</b><b>{rupees(order.balance)}</b></div>}
        </div>

        {order.status === 'Paid' && (
          <>
            <div className="pos-msg ok">Paid in full{order.payments.length ? `: ${order.payments.map((p) => `${p.method} ${rupees(p.amount)}`).join(', ')}` : ''}.</div>
            <div className="pos-actions">
              <button className="pos-btn" onClick={() => navigate(to(`/pos/print/bill/${order.id}?back=${encodeURIComponent(`/pos/order/${order.id}`)}`))}>Reprint bill</button>
              <button className="pos-btn gold" onClick={() => navigate(to('/pos'))}>Back to floor</button>
            </div>
          </>
        )}
        {order.status === 'Cancelled' && <div className="pos-msg err">Cancelled: {order.cancelReason}</div>}

        {!closed && (
          <>
            <div className="pos-actions">
              <button className="pos-btn gold" disabled={busy || unsent === 0} onClick={sendKot}>Send KOT/BOT{unsent ? ` (${unsent})` : ''}</button>
              {can(me.role, 'Cashier')
                ? <button className="pos-btn green" disabled={busy || order.items.every((i) => i.isVoid)} onClick={bill}>{order.billNumber ? 'Re-bill' : 'Bill'}</button>
                : <button className="pos-btn" disabled>Bill (cashier)</button>}
            </div>
            {order.status === 'Billed' && can(me.role, 'Cashier') && <PaymentPanel order={order} busy={busy} onPay={(m, a, r) => act(() => posApi.pay(order.id, m, a, r))} />}
            <div className="pos-actions">
              {order.orderType === 'DineIn' && <button className="pos-btn sm" disabled={busy} onClick={() => setDialog('move')}>Move table</button>}
              {can(me.role, 'Manager') && <button className="pos-btn sm" disabled={busy || order.payments.length > 0} onClick={() => setDialog('discount')}>Discount</button>}
              {order.billNumber && <button className="pos-btn sm" onClick={() => navigate(to(`/pos/print/bill/${order.id}?back=${encodeURIComponent(`/pos/order/${order.id}`)}`))}>Print bill</button>}
              {can(me.role, 'Manager') && <button className="pos-btn sm red" disabled={busy || order.payments.length > 0} onClick={() => setDialog('cancel')}>Cancel order</button>}
            </div>
          </>
        )}
      </aside>

      {picking && (
        <div className="pos-dialog" role="dialog" aria-modal="true" aria-label={`Choose ${picking.name}`} onClick={() => setPicking(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <h3>{picking.name}</h3>
            <div className="pos-items">
              {picking.variants.map((v) => (
                <button key={v.id} className="pos-item" onClick={() => { setPicking(null); add(picking, v.id); }}>
                  <b>{v.name}</b><small>{rupees(v.price)}</small>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {voiding && (
        <ReasonDialog title={`Void ${voiding.name}`} hint="It was already sent to the kitchen/bar. The void is recorded with your name." confirm="Void item"
          reasons={['Customer changed mind', 'Wrong item', 'Spilled / broken', 'Quality issue']}
          onClose={() => setVoiding(null)} onConfirm={async (reason) => { if (await act(() => posApi.voidItem(order.id, voiding.id, reason))) setVoiding(null); }} />
      )}
      {dialog === 'cancel' && (
        <ReasonDialog title={`Cancel order #${order.orderNo}`} hint="Everything on it is written off and recorded in reports." confirm="Cancel order"
          reasons={['Guest left', 'Duplicate order', 'Test order']}
          onClose={() => setDialog(null)} onConfirm={async (reason) => { if (await act(() => posApi.cancel(order.id, reason))) setDialog(null); }} />
      )}
      {dialog === 'discount' && <DiscountDialog order={order} onClose={() => setDialog(null)} onApply={async (d) => { if (await act(() => posApi.discount(order.id, d))) setDialog(null); }} />}
      {dialog === 'move' && <MoveDialog order={order} onClose={() => setDialog(null)} onMove={async (tableId) => {
        if (await act(() => posApi.updateOrder(order.id, { tableId, covers: order.covers, customerName: order.customerName, customerPhone: order.customerPhone, note: order.note }))) setDialog(null);
      }} />}
    </div>
  );
};

function PaymentPanel({ order, busy, onPay }: { order: PosOrder; busy: boolean; onPay: (method: string, amount: number, reference?: string) => void }) {
  const [method, setMethod] = useState<string>('Cash');
  const [amount, setAmount] = useState(String(order.balance));
  const [reference, setReference] = useState('');
  useEffect(() => setAmount(String(order.balance)), [order.balance]);
  const value = Number(amount) || 0;
  const change = method === 'Cash' && value > order.balance ? value - order.balance : 0;
  return (
    <div className="pos-panel" style={{ padding: 10 }}>
      <div className="pos-pay">
        {METHODS.map((m) => <button key={m} type="button" className={`pos-btn sm ${method === m ? 'on' : ''}`} onClick={() => setMethod(m)}>{m}</button>)}
      </div>
      <div className="pos-row" style={{ marginTop: 8 }}>
        <label className="pos-field" style={{ margin: 0 }}>Amount received<input className="pos-input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
        {method !== 'Cash' && <label className="pos-field" style={{ margin: 0 }}>Reference (UTR / card)<input className="pos-input" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={60} /></label>}
      </div>
      {change > 0 && <div className="pos-msg ok" style={{ margin: '8px 0 0' }}>Give change: {rupees(change)}</div>}
      <button className="pos-btn green block" style={{ marginTop: 8 }} disabled={busy || value <= 0} onClick={() => onPay(method, value, reference || undefined)}>
        Record {method} payment
      </button>
    </div>
  );
}

function ReasonDialog({ title, hint, confirm, reasons, onClose, onConfirm }: {
  title: string; hint: string; confirm: string; reasons: string[]; onClose: () => void; onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="pos-dialog" role="dialog" aria-modal="true" aria-label={title}>
      <div>
        <h3>{title}</h3>
        <p className="pos-muted" style={{ marginTop: -6 }}>{hint}</p>
        <div className="pos-tabs">{reasons.map((r) => <button key={r} type="button" className={`pos-tab ${reason === r ? 'on' : ''}`} onClick={() => setReason(r)}>{r}</button>)}</div>
        <label className="pos-field">Reason<input className="pos-input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={120} autoFocus /></label>
        <div className="foot">
          <button className="pos-btn" onClick={onClose}>Back</button>
          <button className="pos-btn red" disabled={!reason.trim()} onClick={() => onConfirm(reason.trim())}>{confirm}</button>
        </div>
      </div>
    </div>
  );
}

function DiscountDialog({ order, onClose, onApply }: { order: PosOrder; onClose: () => void; onApply: (d: { percent?: number | null; amount?: number | null; reason?: string }) => void }) {
  const [kind, setKind] = useState<'percent' | 'amount'>(order.discountFlat ? 'amount' : 'percent');
  const [value, setValue] = useState(String(order.discountPercent ?? order.discountFlat ?? ''));
  const [reason, setReason] = useState(order.discountReason ?? '');
  const n = Number(value) || 0;
  return (
    <div className="pos-dialog" role="dialog" aria-modal="true" aria-label="Discount">
      <div>
        <h3>Discount on #{order.orderNo}</h3>
        <div className="pos-tabs">
          <button type="button" className={`pos-tab ${kind === 'percent' ? 'on' : ''}`} onClick={() => setKind('percent')}>Percent</button>
          <button type="button" className={`pos-tab ${kind === 'amount' ? 'on' : ''}`} onClick={() => setKind('amount')}>Amount (₹)</button>
        </div>
        <div className="pos-row">
          <label className="pos-field">{kind === 'percent' ? 'Percent' : 'Amount'}<input className="pos-input" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} /></label>
          <label className="pos-field">Reason<input className="pos-input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={120} placeholder="e.g. Regular guest" /></label>
        </div>
        <div className="foot">
          {(order.discountAmount > 0) && <button className="pos-btn red" onClick={() => onApply({ percent: null, amount: null })}>Remove discount</button>}
          <button className="pos-btn" onClick={onClose}>Back</button>
          <button className="pos-btn gold" disabled={n <= 0 || !reason.trim()} onClick={() => onApply(kind === 'percent' ? { percent: n, reason } : { amount: n, reason })}>Apply</button>
        </div>
      </div>
    </div>
  );
}

function MoveDialog({ order, onClose, onMove }: { order: PosOrder; onClose: () => void; onMove: (tableId: number) => void }) {
  const [tables, setTables] = useState<PosTable[] | null>(null);
  useEffect(() => { posApi.floor().then((f) => setTables(f.tables.filter((t) => t.isActive && !t.openOrder))).catch(() => setTables([])); }, []);
  return (
    <div className="pos-dialog" role="dialog" aria-modal="true" aria-label="Move table">
      <div>
        <h3>Move #{order.orderNo} from {order.tableName}</h3>
        {tables === null ? <p className="pos-muted">Loading...</p> : tables.length === 0 ? <p className="pos-muted">No free tables.</p> : (
          <div className="pos-tables">{tables.map((t) => <button key={t.id} className="pos-table" onClick={() => onMove(t.id)}><b>{t.name}</b><small>{t.seats} seats</small></button>)}</div>
        )}
        <div className="foot"><button className="pos-btn" onClick={onClose}>Back</button></div>
      </div>
    </div>
  );
}

export default OrderPage;
